import React, { useState } from "react";
import { BrandLogo } from "@/components/Brand";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import {
    Compass, ArrowLeft, ArrowRight, Home, Map, Activity, LifeBuoy,
    Stethoscope, ClipboardList, Library, FileSignature, MessageCircle,
    Users, Heart, Sparkles, CheckCircle2
} from "lucide-react";

const SECTIONS = [
    {
        key: "dashboard", icon: Home,
        title: "Where your day begins",
        subtitle: "One calm place to start",
        body: "Most caregivers wake up to a flood of decisions, lists, and unanswered questions in different apps and notebooks. Memory Lane gathers what matters into one calm starting point — so you can see where things stand, where to focus today, and what can wait until tomorrow.",
        bullets: [
            "Less mental load — your loved one's stage, situation, and concerns in one place",
            "A clear next step for the day, without scrolling through reminders",
            "Help is always one tap away when the day gets heavy",
        ],
        tone: "teal",
    },
    {
        key: "roadmap", icon: Map,
        title: "Knowing what to do next",
        subtitle: "Direction at every stage",
        body: "Most caregivers don't need more information — they need to know what to do right now versus what to prepare for later. The roadmap gives you a concrete action checklist for each stage of the journey, so you stop wondering if you've missed something important.",
        bullets: [
            "What to put in place during early, middle, and late stages",
            "Track what's done — and what still needs your attention",
            "Confidence that the important things are accounted for, on your timeline",
        ],
        tone: "coral",
    },
    {
        key: "behavior", icon: Activity,
        title: "Making sense of what's happening",
        subtitle: "Patterns instead of guesswork",
        body: "When a loved one paces, refuses to bathe, or wakes confused at 3 a.m., it can feel random and frightening. By gently logging what happened, what helped, and what made it worse, the picture becomes clearer — and you walk into the next doctor visit with something concrete to share.",
        bullets: [
            "Quiet patterns become visible — time of day, triggers, what calmed them",
            "A clean summary to bring to the next appointment, without retyping it",
            "A sense of control where it used to feel chaotic",
        ],
        tone: "blue",
    },
    {
        key: "guidance", icon: LifeBuoy,
        title: "Knowing how to respond",
        subtitle: "Practical strategies in plain language",
        body: "Caregivers are expected to handle agitation, wandering, sundowning, and refusal of care — without training. Memory Lane offers gentle, plain-language guidance for the most common moments: what this may mean, what often helps, what tends to make things worse, and how to connect when words have left.",
        bullets: [
            "Specific moves to try (and avoid) for eight common behaviors",
            "Communication ideas for late-stage moments — when presence matters more than words",
            "Less self-doubt about whether you're doing the right thing",
        ],
        tone: "blue",
    },
    {
        key: "appointment", icon: Stethoscope,
        title: "Walking in prepared",
        subtitle: "So nothing important gets left in the parking lot",
        body: "Doctor visits go fast. Memory Lane helps you arrive with the right questions written down, recent changes captured, and the concerns you keep meaning to bring up — so the visit actually addresses what you need, and you leave feeling heard instead of rushed.",
        bullets: [
            "Capture questions and concerns the moment you think of them",
            "Print or save a clean summary to take with you",
            "Walk out with answers, not a longer mental list",
        ],
        tone: "cream",
    },
    {
        key: "patient", icon: ClipboardList,
        title: "When someone else needs to know",
        subtitle: "A printable snapshot for ER, new providers, and family",
        body: "When a new aide arrives, when you're handed an ER clipboard at midnight, or when a sibling steps in for a weekend, the same questions come up. Memory Lane keeps the essentials in one place — meds, allergies, doctors, pharmacy, emergency contacts — ready to print or hand off in seconds.",
        bullets: [
            "Stop retyping the same information at every visit",
            "Confidence that anyone helping has what they need in 30 seconds",
            "Peace of mind in emergencies",
        ],
        tone: "yellow",
    },
    {
        key: "resources", icon: Library,
        title: "Finding the right help, faster",
        subtitle: "Without falling down a Google rabbit hole",
        body: "When a caregiver finally has a moment to search for respite, hospice, legal aid, or a support group, the last thing they need is twelve open tabs and unverified phone numbers. Memory Lane curates trusted starting points for every U.S. state — so the search ends quickly.",
        bullets: [
            "National 24/7 lines and your state's aging agency in one place",
            "Tap a phone number, make the call",
            "Less time hunting, more time with the person you love",
        ],
        tone: "sage",
    },
    {
        key: "navigator", icon: FileSignature,
        title: "When you need a real person",
        subtitle: "Sit with someone who's done this before",
        body: "Some moments don't need information — they need a human being. As Memory Lane grows, families will be able to schedule time with a trained Care Navigator to talk through safety planning, legal documents, difficult decisions, and goals of care. Always at your pace, never rushed.",
        bullets: [
            "Help completing the paperwork families often put off (Living Will, MPOA, DPOA, DNR)",
            "A gentle outside perspective for hard family decisions",
            "Compassionate guidance — without replacing your doctor or attorney",
        ],
        tone: "teal",
    },
    {
        key: "help", icon: MessageCircle,
        title: "On the days that break you",
        subtitle: "Help, the moment you need it",
        body: "Caregiving has hours when nothing helps and everyone is asleep. When the day breaks you, Memory Lane offers a calm, listening place — to vent, to ask, or to find a small, doable next step. Never medical or legal advice. Always knows when to point you to the people who can help.",
        bullets: [
            "A gentle place to feel heard at any hour",
            "Small, practical next steps instead of long lists",
            "Knows the right number to call when it matters",
        ],
        tone: "coral",
    },
    {
        key: "family", icon: Users,
        title: "Asking family for the right kind of help",
        subtitle: "Turn vague offers into real support",
        body: "\"Let me know if you need anything\" is kind — but it doesn't move a meal, fill a prescription, or give you Saturday morning back. Memory Lane helps you put together a clear, specific list of how others can actually help — and share it in one tap.",
        bullets: [
            "Specific tasks family can pick up, instead of guessing",
            "Share by text, email, or print — pre-filled with names and what you need",
            "Less guilt about asking, more help arriving",
        ],
        tone: "blue",
    },
    {
        key: "burnout", icon: Heart,
        title: "Checking in with yourself",
        subtitle: "Because the caregiver matters too",
        body: "Caregivers spend their day asking how someone else is doing — and almost never get the question back. A one-minute weekly check-in gently surfaces how you're really holding up, and points you toward the support you may have been postponing.",
        bullets: [
            "A private moment to notice how you're doing",
            "Gentle suggestions — respite, a doctor visit, a support group",
            "A signal when it's time to ask for more help",
        ],
        tone: "coral",
    },
];

const TONES = {
    teal: { bg: "bg-teal-soft", text: "text-teal-dark", icon_bg: "bg-white text-teal-dark" },
    blue: { bg: "bg-ml_blue-light", text: "text-ml_blue-dark", icon_bg: "bg-white text-ml_blue-dark" },
    cream: { bg: "bg-cream-dark", text: "text-[#4C5551]", icon_bg: "bg-white text-[#1F2624]" },
    coral: { bg: "bg-coral-light", text: "text-coral-dark", icon_bg: "bg-white text-coral-dark" },
    yellow: { bg: "bg-yellow-soft", text: "text-yellow-dark", icon_bg: "bg-white text-yellow-dark" },
    sage: { bg: "bg-sage-light", text: "text-sage-dark", icon_bg: "bg-white text-sage-dark" },
};

export default function Demo() {
    const [active, setActive] = useState("dashboard");
    const current = SECTIONS.find(s => s.key === active);
    const t = TONES[current.tone];

    return (
        <div className="min-h-screen bg-cream">
            <SeoHead
                title="Memory Lane Care — Live Demo (without sign-up)"
                description="Walk through Memory Lane Care without signing up — see the dashboard, the AI navigator Lane, the GUIDE eligibility flow, and how families use the Continuity Passport."
                canonicalPath="/demo"
            />
            <header className="bg-cream/80 backdrop-blur-sm border-b border-[#E2E8F0]/60 sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2.5">
                        <BrandLogo className="h-6" />
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link to="/" data-testid="demo-home" className="text-sm font-medium text-[#4C5551] hover:text-teal-dark px-3 py-2 inline-flex items-center gap-1">
                            <ArrowLeft size={16} strokeWidth={1.6} /> Home
                        </Link>
                        <Link to="/beta" data-testid="demo-beta-cta" className="ml-btn-primary !min-h-[44px] !py-2 !px-4 text-sm">
                            Try Memory Lane
                        </Link>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 animate-fade-in-up">
                <div className="max-w-2xl">
                    <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark flex items-center gap-2">
                        <Sparkles size={16} strokeWidth={1.8} /> 2-minute walkthrough
                    </p>
                    <h1 className="mt-2 text-4xl md:text-5xl font-heading font-semibold tracking-tight">
                        See Memory Lane in action.
                    </h1>
                    <p className="mt-3 text-lg text-[#4C5551]">
                        A guided tour of every tool — Dashboard, Roadmap, Behavior Tracker &amp; Guidance,
                        Appointment Prep, Patient Profile Sheet, Resources, and Care Navigator sessions.
                    </p>
                </div>

                <div className="grid md:grid-cols-12 gap-6 mt-10">
                    {/* Sidebar / chip list */}
                    <aside className="md:col-span-4">
                        <div className="ml-card p-3 sticky top-24" data-testid="demo-sidebar">
                            <ul className="space-y-1">
                                {SECTIONS.map(s => (
                                    <li key={s.key}>
                                        <button
                                            type="button"
                                            data-testid={`demo-link-${s.key}`}
                                            onClick={() => setActive(s.key)}
                                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-colors ${active === s.key
                                                ? "bg-teal-soft text-teal-dark"
                                                : "text-[#4C5551] hover:bg-cream-dark"
                                                }`}
                                        >
                                            <s.icon size={18} strokeWidth={1.6} />
                                            <span className="font-medium text-sm md:text-base">{s.title}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>

                    {/* Feature panel */}
                    <section className="md:col-span-8">
                        <article className={`ml-card p-7 md:p-10 ${t.bg} !border-transparent`} data-testid={`demo-panel-${current.key}`}>
                            <div className={`w-14 h-14 rounded-2xl ${t.icon_bg} flex items-center justify-center mb-4`}>
                                <current.icon strokeWidth={1.5} size={26} />
                            </div>
                            <p className={`text-xs uppercase tracking-wider font-semibold ${t.text}`}>{current.subtitle}</p>
                            <h2 className={`mt-1 text-3xl md:text-4xl font-heading font-medium ${t.text}`}>{current.title}</h2>
                            <p className="mt-4 text-[#1F2624] leading-relaxed">{current.body}</p>
                            <ul className="mt-5 space-y-2.5">
                                {current.bullets.map((b, i) => (
                                    <li key={i} className="flex gap-3 text-[#1F2624]">
                                        <CheckCircle2 size={18} strokeWidth={1.8} className={`mt-0.5 shrink-0 ${t.text}`} />
                                        <span className="leading-relaxed">{b}</span>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    </section>
                </div>

                <div className="mt-12 ml-card p-8 md:p-10 bg-teal-soft !border-teal-soft text-center" data-testid="demo-final-cta">
                    <h2 className="text-2xl md:text-3xl font-heading font-medium text-teal-dark">
                        Ready to walk the path?
                    </h2>
                    <p className="mt-3 text-[#1F2624]">
                        Memory Lane is in private beta. Request access and we&apos;ll be in touch.
                    </p>
                    <Link to="/beta" data-testid="demo-final-cta-link" className="ml-btn-primary mt-6 inline-flex">
                        Request Early Access <ArrowRight size={18} strokeWidth={1.8} />
                    </Link>
                </div>
            </main>
        </div>
    );
}
